/**
 * defa.models.User
 * The Defa User Model
 * @version 1.2.0
 */
import { defa } from 'defa-object';
import Model    from 'scripts/poppet/model';

const apiHost = defa.vars.apiHost;

const api = {
	users   : apiHost + '/users',
	me      : apiHost + '/users/me',
	forgot  : apiHost + '/users/forgotpassword',
	pass    : apiHost + '/users/%s/password',
	email   : apiHost + '/users/%s/email',
	language: apiHost + '/users/%s/language',
	auth    : apiHost + '/auth',
	logout  : apiHost + '/logout',
};

const channelUser = Bb.Radio.channel( 'user' );
const channel     = Bb.Radio.channel( 'objects' );

const User = Model.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	urlRoot: api.users,
	
	defaults: {
		
		// backend
		active  : false,
		email   : '', // email address
		language: '', // preferred language
		
		// transients
		transientEmailVerification : !1, // human readable string if awaiting email verification
		transientHasBluetoothDevice: !1, // allows my devices even if logged out
		transientLoggedIn          : !1, // is currently logged in
		transientRoles             : [], // array of user's roles
		transientUnits             : [], // array of unit id's
	},
	
	completeList: [ 'email' ],
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	// - Save and update management
	
	/**
	 * Parses server responses to make sure all the data gets to it's proper place.
	 * @param  {Object} response  The unparsed server response.
	 * @return {Object}           The parsed server response, with only User params.
	 */
	parse: function ( response ) {
		
		if ( response.customers ) {
			channel.request( 'customers' ).add( response.customers, { merge: true, silent: true } );
		}
		
		if ( response.mapUsersCustomers ) {
			defa.vars.mapUsersCustomers = _.union( defa.vars.mapUsersCustomers, response.mapUsersCustomers );
		}
		
		if ( response.phoneNumbers ) {
			channel.request( 'phoneNumbers' ).add( response.phoneNumbers, { merge: true, silent: true } );
		}
		
		if ( response.units ) {
			channel.request( 'units' ).add( response.units, { merge: true, silent: true } );
		}
		
		if ( response.user ) {
			response = response.user;
		}
		
		if ( response.id ) {
			channel.request( 'users' ).add( response, { silent: true, merge: true } );
		}
		
		return response;
	},
	
	/**
	 * Saves this User's email.
	 * @param {Object} options An object containing the user's new email.
	 * @void
	 */
	saveEmail: function ( options ) {
		
		let self = this;
		
		self.save( {
			email: options.email,
		}, {
			patch: true,
			url  : api.email.replace( '%s', self.get( 'id' ) ),
		} );
	},
	
	/**
	 * Saves this User's preferred language.
	 * @param {Object} options An object containing the user's new language.
	 * @void
	 */
	saveLanguage: function ( options ) {
		
		let self = this;
		
		self.save( {
			language: options.language,
		}, {
			patch: true,
			wait : true,
			url  : api.language.replace( '%s', self.get( 'id' ) ),
		} );
	},
	
	// - Customer management
	
	/**
	 * Gets this User's Customers.
	 * @return {CustomerCollection}  A Collection of Customers tied to this User instance.
	 */
	getCustomers: function () {
		
		let map = _.where( defa.vars.mapUsersCustomers, { user: this.get( 'id' ) } ),
			ids = _.pluck( map, 'customer' );
		
		return channel.request( 'customers' ).filterByIds( ids );
	},
	
	// - Unit management
	
	/**
	 * Gets this User's Units.
	 * @return {UnitCollection}  A Collection of Units tied to this User's instance.
	 */
	getUnits: function () {
		return channel.request( 'units' ).filterByIds( this.get( 'transientUnits' ) );
	},
	
	
	// - Roles management
	
	/**
	 * Checks if this User Instance has a specific role.
	 * @params {array} Array of roles to return true.
	 * @returns {bool} True if the role exists in the roles attribute, false if not.
	 */
	hasRoles: function ( roles ) {
		roles = roles || [];
		roles.push( 'ADMINISTRATOR' );
		
		return ( 1 <= _.intersection( this.get( 'transientRoles' ), roles ).length );
	},
	
	isInstallerAutomotive: function() {
		return this.hasRoles(['INSTALLER_AUTOMOTIVE']);
	},
	isInstallerHome: function() {
		return this.hasRoles(['INSTALLER_HOME']);
	},
	isInstallerInsurance: function() {
		return this.hasRoles(['INSTALLER_AUTOMOTIVE_DENMARK_SECURITY']);
	},
	isInstaller: function() {
		return this.isInstallerAutomotive()
			   || this.isInstallerHome()
			   || this.isInstallerInsurance();
	},
	isTechSupport: function() {
		return this.hasRoles(['TECHNICAL_SUPPORT']);
	},
	
	// - Login and password management
	
	/**
	 * Changes the users password.
	 * @param options {Object} Must include both old password and new password key->value pair.
	 * @void
	 */
	changePassword: function ( options ) {
		options = options || {};
		
		let self = this;
		
		$.ajax( {
			 url        : api.pass.replace( '%s', self.get( 'id' ) ),
			 method     : 'POST',
			 contentType: 'application/json',
			 data       : JSON.stringify( {
				 oldPassword: options.oldPassword,
				 newPassword: options.newPassword,
			 } ),
		 } )
		 .done( function ( response ) {
			 self.trigger( 'sync', self, response );
		 } )
		 .fail( function ( response ) {
			 self.trigger( 'error', self, response );
		 } );
	},
	
	/**
	 * Reports a forgotten password.
	 * @param options {Object} Must include an email parameter.
	 * @void
	 */
	forgotPassword: function ( options ) {
		options = options || {};
		
		if ( options.email ) {
			let self = this;
			
			let data = {
				email: options.email
			};
			
			if (options.ref) {
				data.ref = options.ref
			}
			
			if (options.token) {
				data.token = options.token
			}
			
			$.ajax( {
				 url : api.forgot,
				 data: data,
			 } )
			 .done( function ( response ) {
				 self.trigger( 'success', self, response, options );
			 } )
			 .fail( function ( response ) {
				 self.trigger( 'error', self, response, options );
			 } );
		}
		
	},
	
	/**
	 * Attempts to authenticate with email and password and fetch from the server.
	 * @param options {Object} Must include both an email and a password key->value pair.
	 * @void
	 */
	authenticate: function ( options ) {
		
		options = options || {};
		
		if ( options.email && options.password ) {
			
			// NOTE: the apps have used username instead of email for varname.
			let loginDetails = JSON.stringify( {
				username: options.email,
				password: options.password,
				remember: options.remember,
			} );
			
			
			this.authCall( options );
		}
	},
	
	/**
	 * Actual login call, needs both email and password.
	 * @param options {Object}  Must include both an email and a password key->value pair.
	 * @void
	 */
	authCall: function ( options ) {
		options  = options || {};
		let self = this;
		
		$.ajax( {
			 url        : api.auth,
			 method     : 'POST',
			 contentType: 'application/json',
			 data       : JSON.stringify( {
				 email   : options.email,
				 password: options.password,
				 remember: options.remember,
			 } ),
		 } )
		 .done( function ( response ) {
			 // since this is not a proper fetch, remember to parse the response manually.
			 response = self.parse( response );
			
			 channelUser.request( 'model' ).set( response );
			 self.set( response ).trigger( 'sync', self );
		 } )
		 .fail( function ( response ) {
			 self.trigger( 'error', self, response );
		 } );
	},
	
	/**
	 * Merges the defa.vars user into this user.
	 * @void
	 */ // !TODO: rename to fetchFromVars .. or something more descriptive..
	fetchLoggedIn: function () {
		if ( defa.vars.user ) {
			this.set( _.extend( {}, this.defaults, defa.vars.user ) );
			channel.request( 'users' ).add( this, { silent: true, merge: true } );
		}
		this.trigger( 'first' );
	},
	
	/**
	 * Refresh the currently logged in user.
	 * @void
	 */
	getMe: function () {
		let self = this;
		
		$.get( api.me )
		 .done( function ( response ) {
			 self.set( response );
			 self.trigger( 'fresh' );
		 } );
	},
	
} );

export default User;
